import { useFeedback, useTranslation } from "@opendash/core";
import { _User } from "@opendash/plugin-parse/dist/types-generated";
import { Button, Form, Input, Modal, Select, Space } from "antd";
import Parse from "parse";
import React, { useState } from "react";
import { useLevels } from "../hooks/useLevels";
import { useLocationTypes } from "../hooks/useLocationTypes";
import { useStops } from "../hooks/useStops";
import { useWheelchairBoarding } from "../hooks/useWheelchairBoarding";
import { Level, Wheelchair_Boarding } from "../js/parsegenerated";
import { Location_Type } from "../js/parsegenerated/Location_Type";
import { Stop } from "../js/parsegenerated/Stop";
import { TourId } from "../types/TourId";
import { TimeZoneSelect } from "./TimeZoneSelect";

export interface StopDataType {
  stop_id: string;
  stop_code: string;
  stop_name: string;
  stop_desc: string;
  stop_lat: string;
  stop_lon: string;
  zone_id: string;
  stop_url: string;
  parent_station: string;
  stop_timezone: string;
  wheelchair_boarding: string;
  level_id: string;
  platform_code: string;
  location_type: number;
}

export const StopCreateModal = (props: {
  open: boolean;
  onOk: Function;
  onCancel: Function;
}) => {
  const t = useTranslation();
  const { message } = useFeedback();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [locationTypes, setLocationTypes] = useLocationTypes();
  const [stops, reloadStops] = useStops();
  const [levels, reloadLevels] = useLevels();
  const [wheelchair, reloadWheelchair] = useWheelchairBoarding("hasnoparent");
  const [selectedLocationType, setSelectedLocationType] = useState<number>(0);
  const { TextArea } = Input;
  const { Option } = Select;

  const isRequiredLocationType = (locationTypeValues: number[]) => {
    return locationTypeValues.some((value) => value === selectedLocationType);
  };

  const save = async () => {
    form.validateFields().then(async (values: StopDataType) => {
      setLoading(true);

      try {
        let parentstationobject: Stop;
        let levelobject: Level;
        let wheelchairobject: Wheelchair_Boarding;

        if (values.level_id) {
          levelobject = await new Parse.Query(Level).get(values.level_id);
        }

        if (values.parent_station) {
          parentstationobject = await new Parse.Query(Stop).get(
            values.parent_station
          );
        }

        if (values.wheelchair_boarding) {
          wheelchairobject = await new Parse.Query(Wheelchair_Boarding).get(
            values.wheelchair_boarding
          );
        }

        const locationtypeobject = await new Parse.Query(Location_Type)
          .equalTo("value", values.location_type)
          .first();

        const stop: Stop = new Stop();

        const savedstop = await stop.save({
          stop_id: values.stop_id ? values.stop_id : crypto.randomUUID(),
          stop_code: "" + values.stop_code,
          stop_name: values.stop_name,
          stop_desc: values.stop_desc,
          stop_lat: values.stop_lat,
          stop_lon: values.stop_lon,
          zone_id: values.zone_id,
          stop_url: values.stop_url,
          parent_station: parentstationobject,
          stop_timezone: values.stop_timezone,
          wheelchair_boarding: wheelchairobject,
          level_id: levelobject,
          platform_code: values.platform_code,
          location_type: locationtypeobject,
          tenant: (Parse.User.current() as _User).get("tenant"),
        });

        props.onOk(savedstop);
        form.resetFields();
        setLoading(false);
      } catch (error) {
        console.log(error);
        message.error(t("gtfs:global.message.unknownerror"));
      }
    });
  };

  return (
    <>
      {props.open && (
        <Modal
          modalRender={(modal) => <div id={TourId.Stop.modal}>{modal}</div>}
          forceRender
          title={t("gtfs:stop.create.modal.title")}
          open={props.open}
          onOk={save}
          onCancel={() => {
            props.onCancel();
          }}
          width={"80%"}
          getContainer={false}
          footer={[
            <Button
              id={TourId.Stop.close}
              key="back"
              onClick={() => {
                props.onCancel();
              }}
            >
              {t("gtfs:global.button.cancel_label")}
            </Button>,
            <Button
              id={TourId.Stop.save}
              key="submit"
              type="primary"
              loading={loading}
              onClick={save}
            >
              {t("gtfs:global.button.save_label")}
            </Button>,
          ]}
        >
          <Form
            autoComplete="off"
            form={form}
            layout="vertical"
            onValuesChange={(changedValues, values: StopDataType) => {
              setSelectedLocationType(values.location_type);

              // Reset the input of wheelchar_boarding so no wrong values can be stored.
              if (
                Object.keys(changedValues).some(
                  (changedFormKeys) => changedFormKeys === "location_type"
                )
              ) {
                form.setFieldValue("wheelchair_boarding", null);
              }

              // Reload the data for wheelchair_boarding select
              if (values.location_type === 2) {
                reloadWheelchair("entryorexit");
              } else {
                if (values.parent_station) {
                  reloadWheelchair("hasparent");
                } else if (!values.parent_station) {
                  reloadWheelchair("hasnoparent");
                }
              }
            }}
          >
            <Form.Item
              name="stop_id"
              label={t("gtfs:stop.create.modal.form.id.label")}
              help={t("gtfs:stop.create.modal.form.id.help")}
              rules={[
                {
                  required: false,
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              help="Kurzer Text oder eine Zahl, die Fahrgästen den Ort anzeigt. Diese Codes werden häufig in smartphonebasierten Fahrgastinformationssystemen verwendet oder auf Beschilderungen gedruckt, damit Fahrgäste leichter Informationen zu einem bestimmten Ort erhalten können. Der stop_code kann bei Gebrauch gegenüber der Öffentlichkeit identisch mit einer stop_id sein. Für Orte, an denen Fahrgästen kein Code angezeigt wird, sollte dieses Feld leer bleiben."
              name="stop_code"
              label={t("gtfs:stop.create.modal.form.stop_code.label")}
              rules={[
                {
                  required: false,
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="stop_name"
              help="Name des Orts. Verwenden Sie einen Namen, den Nutzer in der Landessprache und der von Touristen normalerweise verwendeten Sprache verstehen."
              label={t("gtfs:stop.create.modal.form.stop_name.label")}
              rules={[
                {
                  required: isRequiredLocationType([0, 1, 2]),
                  message: t(
                    "gtfs:stop.create.modal.form.stop_name.rules.required"
                  ),
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              help="Beschreibung des Orts, die nützliche und relevante Informationen liefert. Hier sollte nicht einfach nur der Name des Orts eingegeben werden."
              name="stop_desc"
              label={t("gtfs:stop.create.modal.form.stop_desc.label")}
              rules={[
                {
                  required: false,
                },
              ]}
            >
              <TextArea rows={4} />
            </Form.Item>
            <Form.Item>
              <Space.Compact>
                <Form.Item
                  style={{ width: "50%" }}
                  name="stop_lat"
                  label={t("gtfs:stop.create.modal.form.stop_lat.label")}
                  rules={[
                    {
                      required: isRequiredLocationType([0, 1, 2]),
                      message: t(
                        "gtfs:stop.create.modal.form.stop_lat.rules.required"
                      ),
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  style={{ width: "50%" }}
                  name="stop_lon"
                  label={t("gtfs:stop.create.modal.form.stop_lon.label")}
                  rules={[
                    {
                      required: isRequiredLocationType([0, 1, 2]),
                      message: t(
                        "gtfs:stop.create.modal.form.stop_lon.rules.required"
                      ),
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Space.Compact>
            </Form.Item>
            <Form.Item
              help="Gibt die Preiszone für eine Haltestelle an. Dieses Feld ist erforderlich bei Preisinformationen mit fare_rules.txt. Andernfalls ist es optional. Wenn dieser Datensatz eine Station oder einen Stationseingang repräsentiert, wird zone_id ignoriert."
              name="zone_id"
              label={t("gtfs:stop.create.modal.form.zone_id.label")}
              rules={[
                {
                  required: false,
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="stop_url"
              help="URL einer Webseite zu dem Ort. Sollte sich von den Feldwerten für agency.agency_url und routes.route_url unterscheiden."
              label={t("gtfs:stop.create.modal.form.stop_url.label")}
              rules={[
                {
                  required: false,
                  type: "url",
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              initialValue={selectedLocationType}
              name="location_type"
              label={t("gtfs:stop.create.modal.form.location_type.label")}
              rules={[
                {
                  required: false,
                },
              ]}
            >
              <Select>
                {locationTypes.map((locationtype) => (
                  <Option key={locationtype.id} value={locationtype.value}>
                    {locationtype.get("value") +
                      ": " +
                      locationtype.get("name") +
                      " - " +
                      locationtype.get("description")}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            {isRequiredLocationType([0, 2, 3, 4]) ? (
              <Form.Item
                name="parent_station"
                label={t("gtfs:stop.create.modal.form.parent_station.label")}
                rules={[
                  {
                    required: isRequiredLocationType([2, 3, 4]),
                    message: t(
                      "gtfs:stop.create.modal.form.parent_station.rules.required"
                    ),
                  },
                ]}
              >
                <Select>
                  {stops
                    .filter((stop) => {
                      switch (form.getFieldValue("location_type")) {
                        case 0:
                          return stop?.get("location_type")?.get("value") === 1;
                        case 1:
                          return false;
                        case 2:
                          return stop?.get("location_type")?.get("value") === 1;
                        case 3:
                          return stop?.get("location_type")?.get("value") === 1;
                        case 4:
                          return stop?.get("location_type")?.get("value") === 0;
                      }
                    })
                    .map((stop) => (
                      <Option key={stop?.id} value={stop?.id}>
                        {stop?.get("stop_code") + ": " + stop?.get("stop_name")}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            ) : null}
            <Form.Item
              name="stop_timezone"
              label={t("gtfs:stop.create.modal.form.stop_timezone.label")}
              rules={[
                {
                  required: false,
                },
              ]}
            >
              <TimeZoneSelect />
            </Form.Item>
            <Form.Item
              name="wheelchair_boarding"
              label={t("gtfs:stop.create.modal.form.wheelchair_boarding.label")}
              initialValue={wheelchair[0]?.id || null}
              rules={[
                {
                  required: false,
                },
              ]}
            >
              <Select>
                {wheelchair.map((wheely) => (
                  <Option key={wheely.id} value={wheely.id}>
                    {wheely.get("value") + ": " + wheely.get("description")}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="level_id"
              help="Ebene des Orts. Ein und dieselbe Ebene kann von mehreren nicht verbundenen Stationen verwendet werden."
              label={t("gtfs:stop.create.modal.form.level_id.label")}
              rules={[
                {
                  required: false,
                },
              ]}
            >
              <Select>
                {levels.map((level) => (
                  <Option key={level.id} value={level.id}>
                    {level.get("level_index") + ": " + level.get("level_name")}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="platform_code"
              help="Kennung für einen Bahnsteig (Haltestelle, die zu einer Station gehört). Hierfür sollte nur die Bahnsteigkennung verwendet werden (z. B. G oder 3). Begriffe wie „Bahnsteig“ oder „Gleis“ (oder die jeweilige Entsprechung in der Sprache des Feeds) sollten nicht enthalten sein. Das erleichtert Feednutzern die Internationalisierung und Lokalisierung der Bahnsteigkennung für andere Sprachen"
              label={t("gtfs:stop.create.modal.form.platform_code.label")}
              initialValue={"0"}
              rules={[
                {
                  required: false,
                },
              ]}
            >
              <Input style={{ width: "100%" }} />
            </Form.Item>
          </Form>
        </Modal>
      )}
    </>
  );
};
